import React from "react";
import { motion } from "framer-motion";

function Module3() {
  return (
    <>
      <div className="bg-particles" />
      <div className="page-container">
        <motion.div
          className="module-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="module-number">Módulo 03</p>
          <h1 className="module-title">RAG - Knowledge Bases</h1>
          <p className="module-description">
            Retrieval Augmented Generation con Amazon Bedrock Knowledge Bases.
            Dale a Comfamito acceso a documentos reales de Comfama para responder con precision.
          </p>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2>📚 ¿Qué es RAG?</h2>
          <p>
            Retrieval Augmented Generation (RAG) es un patron que combina la capacidad generativa
            de los LLMs con la busqueda en bases de conocimiento externas. En lugar de depender
            solo del entrenamiento del modelo, el agente busca información actualizada antes de responder.
          </p>
          <div className="architecture-diagram">
            <div className="flow">
              <div className="node">Usuario Pregunta</div>
              <div className="arrow">→</div>
              <div className="node">Busqueda Vectorial</div>
              <div className="arrow">→</div>
              <div className="node">Documentos Relevantes</div>
              <div className="arrow">→</div>
              <div className="node">LLM + Contexto</div>
              <div className="arrow">→</div>
              <div className="node">Respuesta Precisa</div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2>🔧 Paso a Paso: Crear Knowledge Base</h2>

          <div className="step-card">
            <h4>Paso 0: Crear Bucket S3</h4>
            <p>
              Crea un bucket S3 privado para almacenar los documentos de Comfama.
              Estos documentos seran la base de conocimiento del agente.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 1: Subir Documentos a S3</h4>
            <p>
              Sube los PDFs, TXTs o Markdowns con las FAQs de Comfama al bucket.
              Puede ser información sobre beneficios, subsidios, programas educativos, etc.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 2: Crear Bedrock Knowledge Base</h4>
            <p>
              En la consola de Amazon Bedrock, crea una nueva Knowledge Base.
              Selecciona el bucket S3 como fuente de datos y vectores de busqueda.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 3: Sincronizar los Datos</h4>
            <p>
              Ejecuta la sincronizacion para que Bedrock indexe los documentos,
              los divida en chunks, y genere embeddings vectoriales.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 4: Obtener IDs Necesarios</h4>
            <p>
              Anota el Knowledge Base ID y el Data Source ID. Los necesitaras
              para integrar RAG con el agente Comfamito.
            </p>
          </div>

          <div className="step-card">
            <h4>Paso 5: Integrar con Comfamito</h4>
            <p>
              Agrega la herramienta "faqs_comfama" al agente LangGraph que
              consulta la Knowledge Base usando la API retrieve de Bedrock.
            </p>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>💻 Integración con LangGraph</h2>
          <div className="code-block">
{`# Tool RAG para Comfamito
import boto3

bedrock_agent = boto3.client("bedrock-agent-runtime", region_name="us-east-1")

KNOWLEDGE_BASE_ID = "<TU_KB_ID>"

def faqs_comfama(query: str) -> str:
    """Busca información en las FAQs de Comfama."""
    response = bedrock_agent.retrieve(
        knowledgeBaseId=KNOWLEDGE_BASE_ID,
        retrievalQuery={"text": query},
        retrievalConfiguration={
            "vectorSearchConfiguration": {
                "numberOfResults": 5
            }
        },
    )

    results = []
    for item in response["retrievalResults"]:
        results.append(item["content"]["text"])

    return "\\n---\\n".join(results)`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>📄 Documentos de Ejemplo</h2>
          <p>
            El folder <code>assets/comfama-faqs/</code> contiene documentos de ejemplo
            que se suben al bucket S3 para crear la Knowledge Base:
          </p>
          <ul>
            <li><strong>beneficios_afiliados.txt:</strong> Informacion sobre beneficios disponibles</li>
            <li><strong>subsidios_vivienda.txt:</strong> Detalles del subsidio de vivienda</li>
            <li><strong>programas_educativos.txt:</strong> Cursos y capacitaciones disponibles</li>
            <li><strong>tienda_comfama.txt:</strong> Catalogo y funcionamiento de la tienda</li>
            <li><strong>eventos_recreacion.txt:</strong> Parques y eventos programados</li>
          </ul>

          <div className="highlight-box">
            <h4>Ventaja del RAG para Comfama</h4>
            <p>
              Cualquier equipo puede actualizar los documentos en S3 y sincronizar la Knowledge Base.
              Comfamito siempre tendra la información más reciente sin necesidad de re-entrenar el modelo.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="source-links-banner">
            <h4>📖 Documentacion Oficial AWS</h4>
            <div className="source-links-list">
              <div className="source-link-item">
                <span>Knowledge Bases →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/knowledge-base.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/knowledge-base.html
                </a>
              </div>
              <div className="source-link-item">
                <span>RAG Retrieve API →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/APIReference/API_agent-runtime_Retrieve.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/APIReference/API_agent-runtime_Retrieve.html
                </a>
              </div>
              <div className="source-link-item">
                <span>S3 User Guide →</span>
                <a href="https://docs.aws.amazon.com/AmazonS3/latest/userguide/Welcome.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/AmazonS3/latest/userguide/Welcome.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Embeddings →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/embeddings.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/embeddings.html
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default Module3;
